import { useEffect, useState } from 'react';
import { X, Loader, PlusCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { getTikTokAuthCode, isMetaEligible, isTikTokEligible } from '../utils/adCreativeEligibility';
import { reconcileTikTokCampaign } from '../utils/tiktokReconcile';
import { applyAdCreativeFilters, type AdCreativeFilters } from '../utils/adCreativeFilters';

interface BulkAddPlatformModalProps {
  releaseId: string;
  releaseName: string | null;
  metaCampaignId: string | null;
  tiktokCampaignId: string | null;
  /** the page's active filters, so the counts match what is on screen */
  filters: AdCreativeFilters;
  onClose: () => void;
  onAddMeta: () => void;
  onAddTikTok: () => void;
}

interface PlatformSummary {
  count: number;
  /** why the platform can't be used, if it can't */
  blockedReason: string | null;
}

const LOADING: PlatformSummary = { count: 0, blockedReason: null };

export function BulkAddPlatformModal({
  releaseId,
  releaseName,
  metaCampaignId,
  tiktokCampaignId,
  filters,
  onClose,
  onAddMeta,
  onAddTikTok,
}: BulkAddPlatformModalProps) {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<PlatformSummary>(LOADING);
  const [tiktok, setTiktok] = useState<PlatformSummary>(LOADING);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);

      // Reconcile BEFORE counting. The campaign can already contain ads that
      // our DB has no record of; without this their creatives would be
      // counted as pending and get added a second time.
      let liveAuthCodes = new Set<string>();
      let tiktokBlocked: string | null = null;
      if (!tiktokCampaignId) {
        tiktokBlocked = 'No TikTok campaign linked to this release';
      } else {
        try {
          const reconciled = await reconcileTikTokCampaign(releaseId, tiktokCampaignId);
          liveAuthCodes = reconciled.liveAuthCodes;
          if (!reconciled.adgroupId) {
            tiktokBlocked = 'Campaign has no ad group yet — create the first ad in TikTok Ads Manager';
          }
        } catch (err) {
          tiktokBlocked = `Could not read the TikTok campaign: ${
            err instanceof Error ? err.message : 'unknown error'
          }`;
        }
      }

      // Read after reconciling so the backfilled ids are included. Filtered
      // the same way the bulk handlers filter, so the counts shown here are
      // exactly what those handlers will act on.
      const { data: rows, error } = await applyAdCreativeFilters(
        supabase.from('ad_creatives').select('*').eq('release_id' as any, releaseId as any),
        filters,
      );

      if (cancelled) return;

      if (error || !rows) {
        setMeta({ count: 0, blockedReason: 'Failed to load creatives' });
        setTiktok({ count: 0, blockedReason: 'Failed to load creatives' });
        setLoading(false);
        return;
      }

      const all = rows as any[];
      setMeta({
        count: all.filter(isMetaEligible).length,
        blockedReason: metaCampaignId ? null : 'No Meta campaign linked to this release',
      });
      setTiktok({
        count: all.filter((c) => {
          if (!isTikTokEligible(c)) return false;
          const code = getTikTokAuthCode(c);
          return !!code && !liveAuthCodes.has(code);
        }).length,
        blockedReason: tiktokBlocked,
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [releaseId, metaCampaignId, tiktokCampaignId, filters.platform, filters.status]);

  // Tailwind only keeps classes it can see as literal strings, so the accent
  // styles are spelled out per platform rather than interpolated.
  const ACCENTS = {
    meta: {
      hover: 'border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20',
      icon: 'text-blue-600',
    },
    tiktok: {
      hover: 'border-gray-200 dark:border-gray-600 hover:border-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20',
      icon: 'text-pink-600',
    },
  } as const;

  const renderOption = (
    label: string,
    summary: PlatformSummary,
    accent: keyof typeof ACCENTS,
    onAdd: () => void,
  ) => {
    const disabled = loading || !!summary.blockedReason || summary.count === 0;
    const note = summary.blockedReason
      ? summary.blockedReason
      : summary.count === 0
        ? 'Nothing left to add — everything eligible is already live'
        : `${summary.count} creative${summary.count === 1 ? '' : 's'} ready to add`;

    return (
      <button
        onClick={onAdd}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-3 p-4 rounded-lg border text-left transition-colors ${
          disabled
            ? 'border-gray-200 dark:border-gray-700 opacity-60 cursor-not-allowed'
            : ACCENTS[accent].hover
        }`}
      >
        <div className="min-w-0">
          <p className="font-medium dark:text-white">
            Add {label} {loading ? '' : `(${summary.count})`}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {loading ? 'Checking…' : note}
          </p>
        </div>
        {loading ? (
          <Loader className="h-4 w-4 animate-spin text-gray-400 shrink-0" />
        ) : (
          <PlusCircle
            className={`h-5 w-5 shrink-0 ${disabled ? 'text-gray-400' : ACCENTS[accent].icon}`}
          />
        )}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full overflow-hidden flex flex-col"
      >
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white">
            Add creatives{releaseName ? ` for "${releaseName}"` : ''}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Choose which campaign to add this release's pending creatives to.
          </p>
          {renderOption('Meta', meta, 'meta', onAddMeta)}
          {renderOption('TikTok', tiktok, 'tiktok', onAddTikTok)}
        </div>

        <div className="flex justify-end p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );
}
